import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { absoluteUrl } from "@/lib/absolute-url";
const wpLogo = { url: "/press-logos/washington-post.webp" };
const espnLogo = { url: "/press-logos/espn.webp" };
const esquireLogo = { url: "/press-logos/esquire.webp" };
const foxLogo = { url: "/press-logos/fox-sports.webp" };
const gqLogo = { url: "/press-logos/gq.webp" };
const nbcLogo = { url: "/press-logos/nbc.webp" };

const braggingLogos = [
  { src: wpLogo.url, alt: "The Washington Post" },
  { src: espnLogo.url, alt: "ESPN" },
  { src: esquireLogo.url, alt: "Esquire" },
  { src: foxLogo.url, alt: "Fox Sports" },
  { src: gqLogo.url, alt: "GQ" },
  { src: nbcLogo.url, alt: "NBC" },
];

const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then((m) => ({ default: m.QuoteForm })));

const TITLE = "About RESTORE Hair | RESTORE® Hair";
const DESCRIPTION =
  "Learn more about RESTORE Hair. Providing exceptional hair restoration solutions led by the medical director Dr. James Harris and an incredible team.";
const HERO_IMG = "/img/r/wp-2022-05-group-289-1.webp";
const HARRIS_IMG = "/img/r/wp-2023-01-dr-harris-scott-1-1.webp";
const SIGN_IMG = "/img/r/wp-2023-01-sign.webp";
const WHY_IMG =
  "/img/r/wp-2025-08-hof-hair-1920x900-ian-v1-3-1-1.webp";

const whyClients = [
  { title: "Hall of Fame Hockey Player", name: "Eddie Olczyk" },
  { title: "Hall of Fame Football Player", name: "Deion Sanders" },
  { title: "Hall of Fame Football Player", name: "Brian Urlacher" },
  { title: "Professional Baseball Player", name: "Ian Happ" },
];

const aboutResults = [
  {
    grafts: "2,152 Grafts",
    name: "Ian Happ",
    role: "Chicago Baseball Player",
    before: "https://assets.restorehair.com/wp/2024/07/ian_h_-min.webp",
    after: "https://assets.restorehair.com/wp/2024/07/ian_h_2-min.webp",
    quote:
      "I never wore my hat in the dugout. When I would see photos of myself during the game, my hair looked like a mess.",
    cta: "Watch Ian's Story",
    href: "https://restorehair.com/results/ian-happ/",
  },
  {
    grafts: "6,800 Grafts",
    name: "Matt Forte",
    role: "Ret. Football Player",
    before: "https://assets.restorehair.com/wp/2024/07/matt_f_-min.webp",
    after: "https://assets.restorehair.com/wp/2024/07/matt_f_2-min.webp",
    quote:
      "It's almost like a rite of passage for a kid, when they go to the barbershop with their dad. I am glad I am able to take my son with me now.",
    cta: "Watch Matt's Story",
    href: "https://restorehair.com/results/matt-forte/",
  },
  {
    grafts: "5,805 Grafts",
    name: "Zach Johnson",
    role: "Pro Tour Golfer",
    before: "https://assets.restorehair.com/wp/2024/06/zach_j_before_front-1.webp",
    after: "https://assets.restorehair.com/wp/2024/06/zach_j_after_front2.webp",
    quote:
      "People can't believe how young I look. I think it's taken about 10 years off of me. I am always camera & spotlight ready!",
    cta: "Watch Zach's Story",
    href: "https://restorehair.com/results/zach-johnson/",
  },
];

const pillars = [
  {
    icon: "/img/r/wp-2022-03-image-14-2-1.webp",
    title: "100% Natural, Undetectable Results",
    body: "No one will know but you that you've had an operation. The results look so natural because it's your own hair — just relocated from your donor region to your thinning areas.",
  },
  {
    icon: "/img/r/wp-2022-03-image-13-1.webp",
    title: "A Dramatically Better Approach",
    body: "Other clinics may harvest donor hair by stripping skin from the back of the head during a hair transplant surgery. At RESTORE, you'll get the results you want, minus the stitches, or linear scar line.",
  },
  {
    icon: "/img/r/wp-2022-03-image-15-1.webp",
    title: "Comfortable Procedure",
    body: "Sit back, stream movies, browse the web or read a book. You'll spend the afternoon relaxing in a posh suite while we RESTORE your hair.",
  },
];

const services = [
  {
    title: "No-Shave F.U.E. Procedure",
    href: "https://restorehair.com/no-shave-fue-hair-transplant/",
    img: "/img/r/wp-2023-12-fue-nav-1080x1080.webp",
    body: "The RESTORE® No-Shave FUE™ Hair Transplant procedure is our highly coveted hair restoration procedure. It's the most discreet and undetectable way to get your hair back if you are experiencing hair loss. Our hair transplant surgeons focus exclusively on hair transplants, unlike others who focused primarily on plastic surgery.",
  },
  {
    title: "Non-Surgical Solutions",
    href: "https://restorehair.com/non-surgical/",
    img: "/img/r/wp-2023-12-everhair-group.webp",
    body: "If you're worried about thinning hair on your scalp but aren't ready for a hair restoration procedure, EVERHAIR is the solution for you. Its effective non-surgical hair regrowth is a quick, easy, and affordable way to help stop hair loss and repair & regrow your hair to take years off your looks.",
  },
  {
    title: "Solutions for Women",
    href: "https://thairapybyrestore.com/",
    img: "/img/r/wp-2022-08-for-women-v1.webp",
    body: "Thairapy is a comprehensive treatment program to help stop hair loss and regrow your hair. Your physician will customize a combination of proven-effective treatments to deliver the best results. It's discreet, individualized — and it really works.",
  },
];

export const Route = createFileRoute("/about-us")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://restorehair.com/about-us/" },
      { property: "og:image", content: absoluteUrl(HERO_IMG) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: absoluteUrl(HERO_IMG) },
    ],
    links: [{ rel: "canonical", href: "https://restorehair.com/about-us/" }],
  }),
  component: AboutUsPage,
});

function AboutUsPage() {
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header />
      <main>
        <section className="py-12 md:py-16">
          <div className="mx-auto grid max-w-6xl items-start gap-10 px-4 lg:grid-cols-2 lg:px-8">
            <img
              src={HERO_IMG}
              alt="RESTORE® Hair clinic lobby"
              width={1000}
              height={886}
              loading="eager"
              className="h-auto w-full object-cover"
            />
            <div>
            <h1 className="text-3xl font-extrabold tracking-tight text-foreground md:text-5xl">
              About RESTORE® Hair
            </h1>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              At RESTORE® Hair, we understand the impact hair loss can have on a person's
              confidence and self-esteem. That's why we are dedicated to providing innovative and
              effective solutions to help you regrow your hair and your confidence.
            </p>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground">
              <span className="font-bold text-foreground">Our mission at RESTORE</span> is to
              provide our clients with exceptional hair transplant results, and deliver a high
              quality, caring, and thoughtful experience before, during, and after every visit. We
              want to empower men and women to look and feel their best by offering personalized
              hair restoration solutions and natural-looking results. We believe that everyone
              deserves to feel confident in their own skin.
            </p>
            </div>
          </div>

          <div className="mx-auto mt-12 grid max-w-6xl gap-8 px-4 md:grid-cols-3 lg:px-8">
            {pillars.map((p) => (
              <div key={p.title} className="text-center">
                <img
                  src={p.icon}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="mx-auto h-[75px] w-[75px] object-contain"
                />
                <h2 className="mt-5 text-xl font-bold text-foreground">{p.title}</h2>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-muted pt-12 md:pt-16">
          <div className="mx-auto max-w-5xl px-4 text-center lg:px-8">
            <div>
              <h2 className="text-3xl font-extrabold text-foreground md:text-5xl">
                Dr. James Harris
              </h2>
              <p className="mt-2 text-sm font-bold uppercase tracking-wider text-primary">
                Medical Director &amp; Hair Restoration Physician
              </p>
              <p className="mx-auto mt-5 max-w-3xl text-base leading-relaxed text-muted-foreground">
                RESTORE® is led by Dr. James Harris, an award-winning hair restoration surgery
                doctor, having performed thousands of procedures throughout his 25 year career.
                He's a renowned author and international public speaker who has trained hundreds of
                doctors from around the world on his innovative hair transplantation methods. He's
                also an esteemed member of ISHRS.
              </p>
              <img
                src={SIGN_IMG}
                alt="Signature of Dr. James Harris"
                loading="lazy"
                className="mx-auto mt-6 h-14 w-auto"
              />
              <div className="mt-6 flex flex-col items-center gap-2">
                <Link
                  to="/doctors/dr-james-harris/"
                  className="text-sm font-bold text-primary hover:underline"
                >
                  Read More about Dr. Harris →
                </Link>
                <Link to="/doctors/" className="text-sm font-bold text-primary hover:underline">
                  Meet our other physicians →
                </Link>
              </div>
            </div>

            <div className="relative mx-auto mt-10 w-full max-w-5xl">
              <div className="relative z-10 mb-6 flex items-start justify-between gap-4 lg:mb-0">
                <div className="text-left lg:absolute lg:left-0 lg:top-0">
                  <span className="mb-1 inline-block bg-primary/30 px-3 py-1 text-sm text-foreground">
                    Award-Winning Hair Doctor
                  </span>
                  <p className="text-lg font-bold text-foreground md:text-xl">Dr. James Harris</p>
                  <p className="text-sm italic text-muted-foreground">RESTORE Medical Director</p>
                </div>
                <div className="text-right lg:absolute lg:right-0 lg:top-0">
                  <p className="text-lg font-bold text-foreground md:text-xl">Scott N</p>
                  <p className="text-sm italic text-muted-foreground">RESTORE Client</p>
                </div>
              </div>
              <img
                src={HARRIS_IMG}
                alt="Dr. James Harris, RESTORE® medical director, with RESTORE client Scott N."
                loading="lazy"
                className="mx-auto -mb-px block h-auto w-full object-contain align-bottom"
              />
            </div>
          </div>
        </section>

        <section className="py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <p className="text-center text-sm font-bold uppercase tracking-widest text-primary">
              The Leaders in Hair Restoration
            </p>
            <h2 className="mt-2 text-center text-3xl font-extrabold text-foreground md:text-4xl">
              Our Services
            </h2>
            <div className="mt-10 grid gap-8 md:grid-cols-3">
              {services.map((s) => (
                <div key={s.title} className="border border-border bg-background">
                  <a href={s.href} target={s.href.includes("restorehair.com") ? undefined : "_blank"} rel="noopener" className="block">
                    <img
                      src={s.img}
                      alt={s.title}
                      loading="lazy"
                      className="h-auto w-full object-contain"
                    />
                  </a>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-foreground">
                      <a
                        href={s.href}
                        target={s.href.includes("restorehair.com") ? undefined : "_blank"}
                        rel="noopener"
                        className="hover:text-primary hover:underline"
                      >
                        {s.title}
                      </a>
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="pt-12 md:pt-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <h2 className="text-center text-3xl font-extrabold text-foreground md:text-5xl">
              Why <span className="text-primary">RESTORE?</span>
            </h2>
            <p className="mx-auto mt-5 max-w-3xl text-center text-base leading-relaxed text-muted-foreground">
              From celebrities and athletes to businessmen and everyman, more than 20,000 men from
              all walks of life have regrown their hair thanks to RESTORE.
            </p>
            <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {whyClients.map((c) => (
                <div key={c.name} className="text-center">
                  <span className="inline-block bg-primary/30 px-3 py-1 text-sm text-foreground">
                    {c.title}
                  </span>
                  <p className="mt-2 text-xl font-extrabold text-foreground">{c.name}</p>
                  <p className="text-sm italic text-muted-foreground">RESTORE® Client</p>
                </div>
              ))}
            </div>
            <div className="relative mt-6">
              <img
                src={WHY_IMG}
                alt="RESTORE clients Eddie Olczyk, Deion Sanders, Brian Urlacher and Ian Happ"
                width={1920}
                height={900}
                loading="lazy"
                className="block h-auto w-full object-cover"
              />
              <a
                href="https://restorehair.com/media-outlet/"
                className="block bg-[hsl(0_0%_35%)] px-6 py-8 md:absolute md:inset-x-0 md:bottom-0 md:mx-auto md:max-w-4xl"
              >
                <p className="text-center text-xl tracking-wide text-white">BRAGGING RIGHTS</p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-8 md:gap-14">
                  {braggingLogos.map((l) => (
                    <img
                      key={l.alt}
                      src={l.src}
                      alt={`${l.alt} logo`}
                      loading="lazy"
                      className="h-8 w-auto object-contain md:h-10"
                    />
                  ))}
                </div>
              </a>
            </div>
          </div>
        </section>

        <section style={{ backgroundColor: "#e9e9e9" }} className="py-12 md:py-16">
          <div className="mx-auto max-w-6xl px-4 lg:px-8">
            <p className="text-center text-sm font-bold text-foreground">Before &amp; After</p>
            <h2 className="mt-3 text-center text-3xl font-extrabold text-foreground md:text-5xl">
              Real Clients, <span className="font-black">Real Results</span>
            </h2>
            <p className="mx-auto mt-6 max-w-3xl text-center text-base leading-relaxed text-muted-foreground">
              Lasting results are the most significant indicator of a successful procedure. From
              celebrities and athletes to businessmen and every man, RESTORE's hair transplant
              before and after patient stories are a testament to our hair surgeon's expertise and
              results.
            </p>
            <p className="mt-6 text-center text-sm italic text-muted-foreground">
              100% authentic RESTORE clients. No photoshopping. No trick photography.
            </p>
            <div className="mt-10 grid gap-8 md:grid-cols-3">
              {aboutResults.map((r) => (
                <div key={r.name} className="flex flex-col bg-[#eeeeee]">
                  <div className="grid h-[240px] grid-cols-2 overflow-hidden">
                    <img
                      src={r.before}
                      alt={`${r.name} before hair transplant`}
                      loading="lazy"
                      className="h-full w-full object-cover object-top"
                    />
                    <img
                      src={r.after}
                      alt={`${r.name} after hair transplant`}
                      loading="lazy"
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="bg-foreground/70 px-5 py-5 text-background">
                    <p className="text-[30px] font-normal leading-9">{r.name}</p>
                    <p className="mt-2 text-base italic leading-5">{r.role}</p>
                    <p className="text-base italic leading-5">RESTORE Client</p>
                    <p className="mt-3 text-sm">{r.grafts}</p>
                  </div>
                  <div className="flex flex-1 flex-col items-center px-6 py-8 text-center">
                    <span aria-hidden="true" className="text-6xl leading-none text-muted-foreground/40">
                      &ldquo;
                    </span>
                    <p className="mt-4 text-base leading-7 text-muted-foreground">{r.quote}</p>
                    <a
                      href={r.href}
                      className="mt-8 inline-block bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition hover:bg-primary/90"
                    >
                      {r.cta}
                    </a>
                  </div>
                </div>
              ))}
            </div>
            <p className="mx-auto mt-10 max-w-5xl text-center text-xs italic leading-relaxed text-muted-foreground">
              Not all patients will experience the outcomes shown in the photographs and described in
              the testimonial statements on this website, and not all persons will be eligible for the
              procedure. Results will depend on a number of factors, including your personal medical
              history. Please ask a physician about your individual case. These clients were
              compensated for their endorsements.
            </p>
          </div>
        </section>

        <Suspense fallback={null}>
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}